import React from 'react';
import { useWS } from '../hooks/useWebSocket';

const EVENT_ICONS = {
  task_created: '📋',
  task_claimed: '🤝',
  task_completed: '💰',
  deal_posted: '📢',
  bid_placed: '🎯',
  deal_closed: '✅',
  agent_joined: '🤖',
  agent_scored: '📈',
  payment_settled: '💸',
  a2a_initiated: '🔄',
  a2a_settled: '🤝',
};

const EVENT_COLORS = {
  task_completed: 'text-emerald-400',
  payment_settled: 'text-emerald-400',
  deal_posted: 'text-blue-400',
  bid_placed: 'text-amber-400',
  a2a_initiated: 'text-purple-400',
  a2a_settled: 'text-purple-400',
  agent_joined: 'text-cyan-400',
  agent_scored: 'text-indigo-400',
};

function formatEventMessage(event) {
  const d = event.data || event;
  const type = event.source_channel || event.event_type || '';

  if (d.earned_usdc) return `${d.agent_id || 'Agent'} earned $${d.earned_usdc} USDC`;
  if (d.amount_usdc && type.includes('settled')) return `$${d.amount_usdc} USDC settled${d.settlement_seconds ? ` in ${d.settlement_seconds}s` : ''}`;
  if (d.title && d.bounty_usdc) return `New bounty: ${d.title} ($${d.bounty_usdc})`;
  if (d.title && d.price_usdc) return `${d.agent_id || 'Agent'} posted ${d.title} @ $${d.price_usdc}`;
  if (d.bid_usdc) return `${d.agent_id || 'Agent'} bid $${d.bid_usdc}`;
  if (d.offer_usdc) return `A2A negotiation: $${d.offer_usdc} offer`;
  if (d.name && d.score !== undefined) return `${d.name} joined (score: ${d.score})`;
  if (d.new_score) return `${d.agent_id || 'Agent'} score: ${d.old_score} → ${d.new_score}`;
  return JSON.stringify(d).slice(0, 80);
}

function getEventType(event) {
  if (event.source_channel) {
    const map = {
      'tasks:new': 'task_created',
      'tasks:claimed': 'task_claimed',
      'tasks:completed': 'task_completed',
      'deals:posted': 'deal_posted',
      'deals:bid': 'bid_placed',
      'deals:closed': 'deal_closed',
      'agents:joined': 'agent_joined',
      'agents:scored': 'agent_scored',
      'payments:settled': 'payment_settled',
      'a2a:negotiation': 'a2a_initiated',
    };
    return map[event.source_channel] || 'default';
  }
  return event.event_type || 'default';
}

export default function CommerceFeed({ limit = 20 }) {
  const { feedEvents } = useWS();
  const events = feedEvents.slice(0, limit);

  if (events.length === 0) {
    return (
      <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
        <div className="text-2xl mb-2">🔄</div>
        <div>Waiting for live commerce events...</div>
      </div>
    );
  }

  return (
    <div className="space-y-1 max-h-[400px] overflow-y-auto">
      {events.map((event, i) => {
        const type = getEventType(event);
        const color = EVENT_COLORS[type] || 'text-gray-400';
        const icon = EVENT_ICONS[type] || '●';

        return (
          <div
            key={i}
            className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-[hsl(var(--secondary))]/30 transition-colors"
          >
            <span className="text-sm mt-0.5">{icon}</span>
            <div className="flex-1 min-w-0">
              <span className={`text-sm ${color}`}>
                {formatEventMessage(event)}
              </span>
            </div>
            <span className="text-xs text-[hsl(var(--muted-foreground))]/50 whitespace-nowrap">
              {event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : 'now'}
            </span>
          </div>
        );
      })}
    </div>
  );
}
